import { FormikHelpers as FormikActions } from 'formik';
import * as React from 'react';

import { TwsAdminApi } from '@weddingspot/ws-api-client';
import { GeneralInformation } from '../../components/venue-management/GeneralInformation';
import { ContactInfo, GeneralInformationFormData, WeddingPacketFileInfo, TwsAdmin } from '../../components/venue-management/types';
import {
    toFormikError,
    WrappedWsApiError,
    WrappedWsApiValidationError,
    WrappedWsApiMessageError,
    WsFormikError,
} from '../../utils/formik/error';
import { LocationSuggestions } from '../../components/venue-management/AddressCard';

interface OwnProps {
    loadingVenueData: boolean;
    currentVenue: TwsAdmin.AvailableVenue | null;

    // Notify parent that an initialization error occurred
    onInitError: () => void;

    // Notify parent that a form error occurred
    onFormError: () => void;

    // Notify parent that form was successfully submitted
    onFormSuccess: () => void;

    // Notify parent that a new venue was created
    onCreateNewVenue: (id: number, name: string) => void;

    setTouched: (touched: boolean) => void;
}

interface OwnState {
    loadingVenueData: boolean;
    choices: TwsAdmin.GeneralInformationChoicesData | null;
    initialValues: GeneralInformationFormData | null;
    weddingPacketFiles: WeddingPacketFileInfo[] | null;
    contacts: TwsAdmin.VendorUserReadData[] | null;
}

export default class GeneralInformaionContainer extends React.Component<OwnProps, OwnState> {
    constructor(props: OwnProps) {
        super(props);

        this.state = {
            loadingVenueData: true,
            choices: null,
            initialValues: null,
            weddingPacketFiles: null,
            contacts: null,
        };

        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleDeleteFile = this.handleDeleteFile.bind(this);
        this.handleUserFromEmail = this.handleUserFromEmail.bind(this);
        this.handleCreateNewContact = this.handleCreateNewContact.bind(this);
        this.handleUpdateContact = this.handleUpdateContact.bind(this);
        this.handleDeleteContact = this.handleDeleteContact.bind(this);
        this.handleSendInvite = this.handleSendInvite.bind(this);
        this.handleGetLocationSuggestionsWithZipCode = this.handleGetLocationSuggestionsWithZipCode.bind(this);
        this.fetchDataAndUpdateState = this.fetchDataAndUpdateState.bind(this);
    }

    componentDidMount() {
        this.fetchDataAndUpdateState();
    }

    componentDidUpdate(prevProps: OwnProps) {
        const curVenue = this.props.currentVenue;
        const prevVenue = prevProps.currentVenue;

        const refetch =
            // Venue ID changed
            (!!curVenue && !!prevVenue && curVenue.venue_id !== prevVenue.venue_id) ||
            // Was creating new venue and now we're updating a venue
            (!prevVenue && !!curVenue) ||
            // Was updating a venue and now we're creating a new venue
            (!!prevVenue && !curVenue);

        if (refetch) {
            this.fetchDataAndUpdateState();
        }
    }

    render() {
        return (
            <div>
                <GeneralInformation
                    onSubmit={this.handleSubmit}
                    currentVenue={this.props.currentVenue}
                    loadingVenueData={this.state.loadingVenueData}
                    choices={this.state.choices}
                    initialValues={this.state.initialValues}
                    weddingPacketFiles={this.state.weddingPacketFiles || []}
                    searchEmail={this.handleUserFromEmail}
                    createNewContact={this.handleCreateNewContact}
                    updateContact={this.handleUpdateContact}
                    deleteContact={this.handleDeleteContact}
                    sendAccountInvite={this.handleSendInvite}
                    deleteFile={this.handleDeleteFile}
                    getLocationSuggestionsWithZipCode={this.handleGetLocationSuggestionsWithZipCode}
                    onFormError={this.props.onFormError}
                    onFormSuccess={this.props.onFormSuccess}
                    onInitError={this.props.onInitError}
                    setTouched={this.props.setTouched}
                />
            </div>
        );
    }

    /**
     * Combine GeneralInfoReadResponseData + VendorUser[] to make GeneralInformationFormData
     *
     * @param values values data
     * @param contacts contacts data
     */
    private mergeData(values: TwsAdmin.GeneralInfoReadResponseData, contacts: TwsAdmin.VendorUserReadData[]) {
        const transContacts = contacts.map((item) => ({
            ...item,
            ...item.user,
            id: item.user.user_id,
            receive_metrics_emails: item.receive_metrics_emails,
        }));

        // Tranform values
        return {
            ...values,
            wedding_packet_files_to_upload: [],
            styles: values.styles.map((s) => s.style_id),
            contacts: transContacts,
        } as GeneralInformationFormData;
    }

    /**
     * Fetch data via handlers and update component state
     */
    private fetchDataAndUpdateState() {
        this.setState({ loadingVenueData: true });

        let toResolve: Promise<any>[] = [];

        // Fetch choices
        const getChoices = TwsAdminApi.getGeneralInformationChoices();
        toResolve.push(getChoices);

        if (this.props.currentVenue !== null) {
            const getValues = TwsAdminApi.getGeneralInformationValues(this.props.currentVenue.venue_id);
            const getContacts = TwsAdminApi.getVenueContacts(this.props.currentVenue.venue_id);
            toResolve.push(getValues);
            toResolve.push(getContacts);
        }

        Promise.all(toResolve)
            .then(
                (
                    values: [TwsAdmin.GeneralInformationChoicesData, TwsAdmin.GeneralInfoReadResponseData?, TwsAdmin.VendorUserReadData[]?]
                ) => {
                    this.setState({ choices: values[0] });

                    const giValues = values[1];
                    const giContacts = values[2];
                    if (!!giValues && !!giContacts) {
                        const transformedData = this.mergeData(giValues, giContacts);
                        this.setState({
                            initialValues: transformedData,
                            weddingPacketFiles: giValues.wedding_packet_files,
                            contacts: giContacts,
                        });
                    }

                    // All done
                    this.setState({ loadingVenueData: false });
                }
            )
            .catch(() => {
                this.props.onInitError();
            });
    }

    /**
     * Convert data from form into API format
     *
     * @param formData data from form
     */
    private getGeneralPostData(formData: GeneralInformationFormData): TwsAdmin.GeneralInfoWriteData {
        // Use destructuring to first copy matching fields
        let { contacts, wedding_packet_files_to_upload, ...data } = formData;

        // Sanitize videos array (remove any that have empty string and normalize the order)
        data.videos = data.videos
            .filter((item) => !!item.url)
            .map((item, idx) => ({
                ...item,
                order: idx + 1,
            }));

        return data;
    }

    /**
     * Convert form data to wedding packet files data
     *
     * @param formData data from form
     */
    private readWeddingPacketFiles(formData: GeneralInformationFormData): Promise<TwsAdmin.WeddingPacketFileWriteData[]> {
        const fileObjs = formData.wedding_packet_files_to_upload;

        const readFile = (file: File) =>
            new Promise<TwsAdmin.WeddingPacketFileWriteData>((resolve, reject) => {
                const reader = new FileReader();

                reader.readAsDataURL(file);
                reader.onload = () => {
                    const result = reader.result;
                    if (typeof result === 'string') {
                        resolve({
                            name: file.name,
                            file_b64: result,
                        });
                    } else {
                        reject(null);
                    }
                };
                reader.onerror = (_) => reject(null);
            });

        const reads = fileObjs.map((item) => readFile(item));

        return Promise.all(reads);
    }

    private handleSubmit(formData: GeneralInformationFormData, formikActions: FormikActions<GeneralInformationFormData>): Promise<void> {
        formikActions.setSubmitting(true);
        formikActions.setStatus(undefined);
        this.setState({ loadingVenueData: true });

        // Transform form data to something the API can uderstand
        const generalPostData = this.getGeneralPostData(formData);

        const venueId = !!this.props.currentVenue ? this.props.currentVenue.venue_id : null;

        // Update or create
        let isCreateNew = false;
        let write: Promise<TwsAdmin.GeneralInfoReadResponseData>;
        if (!!venueId) {
            write = TwsAdminApi.updateGeneralInformation(venueId, generalPostData);
        } else {
            write = TwsAdminApi.createGeneralInformation(generalPostData);
            isCreateNew = true;
        }

        const postGeneral = write
            .then((res: TwsAdmin.GeneralInfoReadResponseData) => {
                /*
                Do we need to re-initialize w/ data from API?
                If we do, we need to apply errors AFTER intial state is applied which
                is tricky...
                */

                // Update current venue
                this.props.onCreateNewVenue(res.venue_id, res.venue_name);

                // Reset touched
                formikActions.setTouched({});

                // Clean up empty videos
                formikActions.setFieldValue('videos', res.videos);
            })
            .catch((err: WrappedWsApiError<TwsAdmin.GeneralInfoWriteData>) => {
                // Handle proper API responses
                if (!!err.detail && !!err.status) {
                    if (err.status === 400) {
                        const validationError: WrappedWsApiValidationError<TwsAdmin.GeneralInfoWriteData> = err;

                        let transformed = toFormikError<TwsAdmin.GeneralInfoWriteData>(validationError);

                        formikActions.setErrors(transformed);
                    } else {
                        const msgError = err as WrappedWsApiMessageError;
                        formikActions.setStatus(msgError.detail);
                    }
                } else {
                    formikActions.setStatus('An unknown error occurred while attemping' + ' to save values. Please contact support');
                }

                // Rethrow so we can handle downstream
                throw err;
            });

        let postFiles = new Promise<void>((resolve) => resolve());
        if (!!venueId) {
            // Upload files
            const fileFieldName = 'wedding_packet_files_to_upload';
            postFiles = new Promise((resolve, reject) => {
                this.readWeddingPacketFiles(formData)
                    .then((res: TwsAdmin.WeddingPacketFileWriteData[]) => {
                        if (res.length > 0) {
                            TwsAdminApi.uploadFiles(venueId, { files: res })
                                .then((results: TwsAdmin.WeddingPacketFilesData) => {
                                    this.setState({
                                        weddingPacketFiles: !!this.state.weddingPacketFiles
                                            ? this.state.weddingPacketFiles.concat(results.files)
                                            : results.files,
                                    });
                                    formikActions.setFieldValue(fileFieldName, []);
                                    resolve();
                                })
                                .catch((err: WrappedWsApiError<TwsAdmin.WeddingPacketFilesData>) => {
                                    const unknowFieldError =
                                        'An unknown error occurred while attempting to upload files.' + ' Please try again later';

                                    // Handle proper API responses
                                    if (!!err.detail && !!err.status) {
                                        // API client doesn't publish this type
                                        type reqType = { files: string };

                                        if (err.status === 400) {
                                            const validationError = err as WrappedWsApiValidationError<reqType>;
                                            let transformed = toFormikError<reqType>(validationError);
                                            if (!!transformed.files) {
                                                const filesError = transformed.files;

                                                if (!!filesError) {
                                                    formikActions.setFieldError(fileFieldName, filesError);
                                                } else {
                                                    formikActions.setFieldError(fileFieldName, unknowFieldError);
                                                }
                                            }
                                        } else if (err.status === 413) {
                                            formikActions.setFieldError(
                                                fileFieldName,
                                                "The total size of the files you've attempted to upload" +
                                                    ' is too big. Please try uploading fewer/smaller files'
                                            );
                                        } else {
                                            formikActions.setFieldError(fileFieldName, unknowFieldError);
                                        }
                                    } else {
                                        formikActions.setFieldError(fileFieldName, unknowFieldError);
                                    }

                                    reject();
                                });
                        } else {
                            resolve();
                        }
                    })
                    .catch((err) => {
                        formikActions.setFieldError(
                            fileFieldName,
                            'One or more files could not be read. ' + 'Please try again with different files'
                        );
                        reject();
                    });
            });
        }

        return new Promise<void>((resolve, reject) => {
            Promise.all([postGeneral, postFiles])
                .then(() => {
                    resolve();

                    // Notify parent
                    this.props.onFormSuccess();
                })
                .catch(() => {
                    reject();

                    // Notify parent
                    this.props.onFormError();
                })
                .then(() => this.setState({ loadingVenueData: false }));
        });
    }

    private handleDeleteFile(fileId: number, formikActions: FormikActions<GeneralInformationFormData>): Promise<void> {
        if (!!this.props.currentVenue) {
            const venueId = this.props.currentVenue.venue_id;

            // Don't have type for this in API client
            type reqType = { files: number[] };

            return TwsAdminApi.deleteFiles(venueId, { files: [fileId] })
                .then(() => {
                    // Remove the target file from list
                    if (!!this.state.weddingPacketFiles) {
                        this.setState({
                            weddingPacketFiles: this.state.weddingPacketFiles.filter((item) => item.file_id !== fileId),
                        });
                    }
                })
                .catch((err: WrappedWsApiError<reqType>) => {
                    const fieldName = 'wedding_packet_files_to_upload';

                    if (err.status === 400) {
                        const validationError = err as WrappedWsApiValidationError<reqType>;

                        if (!!validationError.detail.content.files) {
                            formikActions.setFieldError(fieldName, validationError.detail.content.files[0]);
                        } else {
                            formikActions.setFieldError(
                                fieldName,
                                'An unknown error occurred while uploading files. Please' + ' try again later.'
                            );
                        }
                    } else {
                        formikActions.setFieldError(
                            fieldName,
                            'An unknown error occurred while uploading files. Please' + ' try again later.'
                        );
                    }
                });
        } else {
            // Shouldn't happen, but don't do anything
            return new Promise((resolve, reject) => resolve());
        }
    }

    private handleUserFromEmail(email: string): Promise<ContactInfo | null> {
        return new Promise((resolve, reject) => {
            TwsAdminApi.getUserWithEmail(email)
                .then((res: TwsAdmin.UserReadData) => {
                    const existingContacts = this.state.contacts;
                    let permissions = {
                        is_primary: false,
                        receive_appointments: false,
                        receive_invoices: false,
                        receive_approval_requests: false,
                        receive_metrics_emails: false,
                        access_venue_metrics: false,
                    };
                    if (!!existingContacts) {
                        let match = existingContacts.find((item) => item.user.email === email);
                        if (!!match) {
                            permissions = {
                                is_primary: match.is_primary,
                                receive_appointments: match.receive_appointments,
                                receive_invoices: match.receive_invoices,
                                receive_approval_requests: match.receive_approval_requests,
                                receive_metrics_emails: match.receive_metrics_emails,
                                access_venue_metrics: match.access_venue_metrics,
                            };
                        }
                    }

                    // Graft the user's permissions unto the final object cause
                    // the API currently doesn't return this data...
                    const composed: ContactInfo = {
                        id: res.user_id,
                        ...res,
                        ...permissions,
                    } as ContactInfo;

                    resolve(composed);
                })
                .catch(() => {
                    resolve(null);
                });
        });
    }

    private handleCreateNewContact(info: ContactInfo): Promise<ContactInfo[]> {
        if (!!this.props.currentVenue) {
            const venueId = this.props.currentVenue.venue_id;

            return new Promise((resolve, reject) => {
                TwsAdminApi.writeVenueContact(venueId, {
                    is_primary: info.is_primary,
                    receive_appointments: info.receive_appointments,
                    receive_invoices: info.receive_invoices,
                    receive_approval_requests: info.receive_approval_requests,
                    receive_metrics_emails: info.receive_metrics_emails,
                    access_venue_metrics: info.access_venue_metrics,
                    user: {
                        first_name: info.first_name,
                        last_name: info.last_name,
                        email: info.email,
                        phone_number: info.phone_number,
                    },
                })
                    .then((res: TwsAdmin.VendorUserReadData[]) => {
                        resolve(
                            res.map((item) => ({
                                ...item.user,
                                ...item,
                                id: item.user.user_id,
                            }))
                        );
                    })
                    .catch((err: WrappedWsApiError<TwsAdmin.VendorUserWriteData>) => {
                        const fieldName = '';

                        if (err.status === 400) {
                            const validationError = err as WrappedWsApiValidationError<TwsAdmin.VendorUserWriteData>;
                            const transformed = toFormikError<TwsAdmin.VendorUserWriteData>(validationError);

                            // Apply another transformation to get something we
                            // can map back to the ContactInfo type
                            const formTransformed: WsFormikError<ContactInfo> = {
                                __errors__: transformed.__errors__,
                                first_name: !!transformed.user ? transformed.user.first_name : undefined,
                                last_name: !!transformed.user ? transformed.user.last_name : undefined,
                                email: !!transformed.user ? transformed.user.email : undefined,
                                phone_number: !!transformed.user ? transformed.user.phone_number : undefined,
                                is_primary: transformed.is_primary,
                                receive_appointments: transformed.receive_appointments,
                                receive_invoices: transformed.receive_invoices,
                                receive_approval_requests: transformed.receive_approval_requests,
                                receive_metrics_emails: transformed.receive_metrics_emails,
                                access_venue_metrics: transformed.access_venue_metrics,
                            };
                            reject(formTransformed);
                        } else {
                            reject({ __errors__: 'An unknown error occurred, please try again later.' });
                        }
                    });
            });
        } else {
            // do nothing
            return new Promise((resolve, reject) => {
                if (!!this.state.contacts) {
                    resolve(
                        this.state.contacts.map((item) => ({
                            ...item.user,
                            ...item,
                            id: item.user.user_id,
                        }))
                    );
                } else {
                    resolve([]);
                }
            });
        }
    }

    private handleUpdateContact(data: ContactInfo): Promise<ContactInfo[]> {
        const currentVenue = this.props.currentVenue;

        // Do nothing
        if (!currentVenue) {
            return new Promise((resolve, reject) => {
                if (!!this.state.contacts) {
                    resolve(
                        this.state.contacts.map((item) => ({
                            ...item.user,
                            ...item,
                            id: item.user.user_id,
                        }))
                    );
                } else {
                    resolve([]);
                }
            });
        }

        const { id, ...dataWithoutId } = data;
        const transformed: TwsAdmin.VendorUserWriteData = {
            user: {
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email,
                phone_number: data.phone_number,
            },
            is_primary: data.is_primary,
            receive_appointments: data.receive_appointments,
            receive_invoices: data.receive_invoices,
            receive_approval_requests: data.receive_approval_requests,
            receive_metrics_emails: data.receive_metrics_emails,
            access_venue_metrics: data.access_venue_metrics,
        };

        return new Promise((resolve, reject) => {
            TwsAdminApi.writeVenueContact(currentVenue.venue_id, transformed)
                .then((res: TwsAdmin.VendorUserReadData[]) =>
                    resolve(
                        res.map((item) => ({
                            ...item.user,
                            ...item,
                            id: item.user.user_id,
                        }))
                    )
                )
                .catch((err: WrappedWsApiError<TwsAdmin.VendorUserWriteData>) => {
                    const validationError = err as WrappedWsApiValidationError<TwsAdmin.VendorUserWriteData>;
                    const transformed = toFormikError<TwsAdmin.VendorUserWriteData>(validationError);

                    // Apply another transformation to get something we
                    // can map back to the ContactInfo type
                    const formTransformed: WsFormikError<ContactInfo> = {
                        __errors__: transformed.__errors__,
                        first_name: !!transformed.user ? transformed.user.first_name : undefined,
                        last_name: !!transformed.user ? transformed.user.last_name : undefined,
                        email: !!transformed.user ? transformed.user.email : undefined,
                        phone_number: !!transformed.user ? transformed.user.phone_number : undefined,
                        is_primary: transformed.is_primary,
                        receive_appointments: transformed.receive_appointments,
                        receive_invoices: transformed.receive_invoices,
                        receive_approval_requests: transformed.receive_approval_requests,
                        receive_metrics_emails: transformed.receive_metrics_emails,
                        access_venue_metrics: transformed.access_venue_metrics,
                    };
                    reject(formTransformed);
                });
        });
    }

    private handleDeleteContact(contactId: number): Promise<ContactInfo[]> {
        const currentVenue = this.props.currentVenue;

        // Do nothing
        if (!currentVenue) {
            return new Promise((resolve, reject) => {
                if (!!this.state.contacts) {
                    resolve(
                        this.state.contacts.map((item) => ({
                            ...item.user,
                            ...item,
                            id: item.user.user_id,
                        }))
                    );
                } else {
                    resolve([]);
                }
            });
        }

        return new Promise((resolve, reject) => {
            TwsAdminApi.deleteVenueContact(currentVenue.venue_id, contactId)
                .then((res: TwsAdmin.VendorUserReadData[]) =>
                    resolve(
                        res.map((item) => ({
                            ...item.user,
                            ...item,
                            id: item.user.user_id,
                        }))
                    )
                )
                .catch(() => {
                    reject({ __errors__: 'An unknown error occurred, please try again later' });
                });
        });
    }

    private handleSendInvite(contactInfo: ContactInfo): Promise<void> {
        const currentVenue = this.props.currentVenue;

        // Do nothing
        if (!currentVenue) {
            return new Promise((resolve, reject) => resolve());
        }

        return new Promise((resolve, reject) => {
            const transformed = {
                ...contactInfo,
                user: {
                    first_name: contactInfo.first_name,
                    last_name: contactInfo.last_name,
                    email: contactInfo.email,
                    phone_number: contactInfo.phone_number,
                },
            };

            TwsAdminApi.sendAccountInvite(currentVenue.venue_id, contactInfo.id, transformed)
                .then((res) => resolve())
                .catch((err) => reject(err));
        });
    }

    private handleGetLocationSuggestionsWithZipCode(zip: string): Promise<LocationSuggestions> {
        return new Promise<LocationSuggestions>((resolve, reject) => {
            TwsAdminApi.getMarketingAreasFromZipCode(zip)
                .then((res: TwsAdmin.MarketingArea[]) => {
                    const markets = [];
                    const regions = [];

                    for (var i = 0; i < res.length; i++) {
                        const ma = res[i];
                        var name = ma.name;
                        var group = ma.group;
                        if (group === 1) {
                            markets.push(name);
                        } else if (group === 2) {
                            regions.push(name);
                        }
                    }

                    resolve({
                        markets: markets,
                        regions: regions,
                    });
                })
                .catch(reject);
        });
    }
}
